import type { BaseCandidate, CandidateSortField, SortState } from './types';
import { districtSortKey } from '../../utils/districtDisplay';

/**
 * Computed columns — sort client-side on the directory (see development-workflow.mdc:
 * avoids invalid API ORDER BY on SQL aliases and unnecessary refetch on sort click).
 */
export const CLIENT_ONLY_SORT_FIELDS = new Set<CandidateSortField>([
  'total_funds_raised',
  'seat_incumbent_name',
]);

/**
 * Client-side sort utility for candidate data
 */
export function applySortToData<T extends BaseCandidate>(
  data: T[],
  sort: SortState
): T[] {
  if (!data.length) return data;

  return [...data].sort((a, b) => {
    let aVal: string | number = '';
    let bVal: string | number = '';

    switch (sort.field) {
      case 'name':
        // Candidates display as "Last, First"; sort to match.
        aVal = `${a.lastName || a.fullName || ''}, ${a.firstName || ''}`;
        bVal = `${b.lastName || b.fullName || ''}, ${b.firstName || ''}`;
        break;
      case 'office_sought':
        aVal = a.officeSought || '';
        bVal = b.officeSought || '';
        break;
      case 'district':
        // Canonical order: CO House → CO Senate → US House, numeric within —
        // a plain string compare put '10' before '2'.
        aVal = districtSortKey(a.district);
        bVal = districtSortKey(b.district);
        break;
      case 'party':
        aVal = a.partyAffiliation || '';
        bVal = b.partyAffiliation || '';
        break;
      case 'election_year':
        aVal = a.electionYear || 0;
        bVal = b.electionYear || 0;
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'total_funds_raised':
        aVal = a.totalFundsRaised ?? 0;
        bVal = b.totalFundsRaised ?? 0;
        break;
      case 'seat_incumbent_name': {
        const aName = (a.seatIncumbentName || '').trim();
        const bName = (b.seatIncumbentName || '').trim();
        const aEmpty = !aName;
        const bEmpty = !bName;
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        const comparison = aName.localeCompare(bName);
        return sort.direction === 'asc' ? comparison : -comparison;
      }
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    const comparison = String(aVal).localeCompare(String(bVal));
    return sort.direction === 'asc' ? comparison : -comparison;
  });
}
